import {
    catchError,
    debounceTime,
    filter,
    flatMap,
    switchMap,
} from "rxjs/operators"
import { combineEpics, ofType, StateObservable } from "redux-observable"
import { concat, merge, from, of, EMPTY } from "rxjs"

import { Epic } from "../../epics"
import {
    verifyIfBookmarkGridTile,
    verifyIfWelcomePageContentGridTile,
    verifyIfShortcutGridTile,
} from "../../apis/grid"
import { GlobalState } from "../../store"

import { addErrorNotification } from "../notifications/actions"
import { initializeBookmarks } from "../bookmarks/actions"
import { initializeShortcuts } from "../shortcuts/actions"

import * as actions from "./actions"
import { getTileIds } from "../../util/Grid"
import { RequestCancelledError } from "../../errors"
import { IGrid, NavigationWorkspace } from "../../models"
import { GridTileDTO } from "../../apis/models/dto/grid"

export const defaultRequestingWorkspacesErrorMessage =
    "Something went wrong when fetching workspaces."

export const defaultRequestingGridErrorMessage =
    "Something went wrong when fetching the welcome page grid."
export const defaultRequestingGridReorderErrorMessage =
    "Something went wrong when reordering the welcome page grid."

const filterGridTiles = (tile: GridTileDTO): boolean =>
    verifyIfBookmarkGridTile(tile) ||
    verifyIfWelcomePageContentGridTile(tile) ||
    verifyIfShortcutGridTile(tile)

export const initializeEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        switchMap(() => {
            const { filter, offset, limit } =
                state$.value.welcomePage.workspacesInfo
            return merge(
                of(initializeBookmarks()),
                of(initializeShortcuts()),
                of(actions.requestWorkspaces(filter, offset, limit)),
                of(actions.requestWelcomePageGrid()),
            )
        }),
    )

export const requestWorkspacesEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    { welcomePage: { navigationAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_WORKSPACES),
        debounceTime(500),
        switchMap(({ filter, offset, limit }: actions.RequestWorkspaces) => {
            // making sure that we only search in workspace names
            if (filter.length > 0) {
                filter += ":"
            }
            return from(
                navigationAPI.getWorkspaceFullHierarchy(
                    filter,
                    offset,
                    limit + 1,
                ),
            ).pipe(
                flatMap((workspaces: NavigationWorkspace[]) => {
                    const hasMore = workspaces.length > limit
                    workspaces = workspaces.slice(0, limit)
                    return of(actions.receiveWorkspaces(workspaces, hasMore))
                }),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(
                            actions.receiveWorkspaces(
                                state$.value.welcomePage.workspacesInfo
                                    .workspaces || [],
                                false,
                            ),
                        ),
                        of(
                            addErrorNotification(
                                defaultRequestingWorkspacesErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )

export const requestWelcomePageGridEpic: Epic = (
    action$,
    state$,
    { welcomePage: { gridAPI } },
) =>
    action$.pipe(
        filter(() => !!state$.value.welcomePage.grid.id),
        ofType(actions.REQUEST_GRID),
        switchMap(() =>
            from(gridAPI.getWelcomePageGrid(filterGridTiles)).pipe(
                flatMap((grid: IGrid) =>
                    of(actions.receiveWelcomePageGrid(grid)),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(
                            actions.receiveWelcomePageGrid({
                                id: -1,
                                tiles: [],
                            }),
                        ),
                        of(
                            addErrorNotification(
                                defaultRequestingGridErrorMessage,
                            ),
                        ),
                    )
                }),
            ),
        ),
    )

export const gridUpdateTilesOrderEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    { welcomePage: { gridAPI } },
) =>
    action$.pipe(
        ofType(actions.UPDATE_TILES_ORDER),
        switchMap(() => {
            const grid = state$.value.welcomePage.grid
            const tileIds = getTileIds(grid.tiles)

            return from(
                gridAPI.updateTilesOrder(grid.id, tileIds, filterGridTiles),
            ).pipe(
                flatMap((grid: IGrid) =>
                    of(actions.receiveWelcomePageGrid(grid)),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(
                            actions.receiveWelcomePageGrid(
                                state$.value.welcomePage.grid,
                            ),
                        ),
                        of(
                            addErrorNotification(
                                defaultRequestingGridReorderErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )

export default combineEpics(
    initializeEpic,
    requestWorkspacesEpic,
    requestWelcomePageGridEpic,
    gridUpdateTilesOrderEpic,
)
